import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchBlogPostBySlug, clearBlogPost } from '../store/actions/blogActions';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import styled from 'styled-components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const PostTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 2rem;
`;

const PostBody = styled.div`
  max-width: 42rem;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.foreground};
  white-space: pre-line;
`;

const NotFoundWrap = styled.div`
  text-align: center;
  padding: 4rem 0;
`;

function BlogPost() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const { slug } = useParams();
  const { post, loading, error } = useSelector((state) => state.blog);

  useEffect(() => {
    dispatch(fetchBlogPostBySlug(slug));
    return () => dispatch(clearBlogPost());
  }, [dispatch, slug]);

  if (loading && !post) {
    return (
      <PageLayout>
        <Navbar />
        <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
          <BackLink to={prependPath('/blog')}><ArrowLeft size={16} strokeWidth={2.5} /> Back to Blog</BackLink>
          <p style={{ color: 'var(--muted-foreground)' }}>Loading post…</p>
        </Container>
        <Footer />
      </PageLayout>
    );
  }

  if (!post) {
    return (
      <PageLayout>
        <SeoHead title={`Post not found | CiteMatch`} noindex />
        <Navbar />
        <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
          <BackLink to={prependPath('/blog')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>
          <NotFoundWrap>
            <p style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Post not found</p>
            {error && <p style={{ color: 'var(--crimson)', marginTop: '0.5rem' }}>{error}</p>}
            <Link to={prependPath('/blog')} style={{ marginTop: '0.5rem', display: 'inline-block', color: 'var(--primary)' }}>View all posts</Link>
          </NotFoundWrap>
        </Container>
        <Footer />
      </PageLayout>
    );
  }

  const description =
    (post?.seo?.description && String(post.seo.description)) ||
    (post?.body ? String(post.body).replace(/\s+/g, ' ').trim().slice(0, 160) : undefined);

  return (
    <PageLayout>
      <SeoHead
        title={post?.seo?.title ? String(post.seo.title) : `${post.title} | CiteMatch`}
        description={description}
      />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/blog')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>
        <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <PostTitle>{post.title}</PostTitle>
          <PostMeta>
            <Calendar size={16} /> {post.date ? new Date(post.date).toLocaleDateString() : ''}
          </PostMeta>
          <PostBody>{post.body}</PostBody>
        </motion.article>
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default BlogPost;
